import React, { useState, useEffect } from "react";
import "./Integrations.css";
import { FaWhatsapp, FaInstagram, FaEnvelope, FaPlug, FaGlobe } from "react-icons/fa";
import { FiEdit2, FiTrash2, FiPlus, FiFilter, FiChevronLeft, FiChevronRight, FiActivity } from "react-icons/fi";
import EvolutionWhatsApp from "./Modals/EvolutionWhatsApp";
import Email from "./Modals/Email";
import SiteIntegration from "./Modals/SiteIntegration";
import AddIntegrationModal from "./Modals/AddIntegrationModal";
import EditIntegrationModal from "./Modals/EditIntegrationModal";
import DeleteConfirmationModal from "./Modals/DeleteConfirmationModal";
import FilterModal from "./Modals/FilterModal";
import { useIntegrations } from "../../hooks/useIntegrations";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";
import { api } from "../../services/api";
import usePaymentGuard from "../../hooks/usePaymentGuard";
import PaymentBlockModal from "../PaymentBlockModal/PaymentBlockModal";

const ICON_MAP = {
  whatsapp: FaWhatsapp,
  email: FaEnvelope,
  instagram: FaInstagram,
  site: FaGlobe,
  web: FaGlobe,
  other: FaPlug,
};

const Integrations = () => {
  const {
    integrations,
    providers,
    loading,
    providersLoading,
    error,
    fetchIntegrations,
    deleteIntegration,
  } = useIntegrations();

  // Hook para verificar se pode executar ações (requer pagamento)
  const {
    needsPayment,
    showBlockModal,
    closeBlockModal,
    showPaymentRequired,
  } = usePaymentGuard();

  const [activeProviderKey] = useState(null);
  const [activeIntegration, setActiveIntegration] = useState(null);
  const [configModalType, setConfigModalType] = useState(null);


  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({ channel: '', label: '', status: '' });


  const [testingConnections, setTestingConnections] = useState({});



  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);


  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalProvider, setAddModalProvider] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editIntegration, setEditIntegration] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);


  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth <= 768) {
        setItemsPerPage(3);
      } else {
        setItemsPerPage(6);
      }
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);


  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const handleAddNew = (providerKey = null, providerName = null) => {
    // Verificar se precisa de pagamento antes de permitir adicionar integração
    if (needsPayment) {
      showPaymentRequired();
      return;
    }
    setAddModalProvider(providerKey ? { key: providerKey, name: providerName } : null);
    setAddModalOpen(true);
  };

  const handleCreateIntegration = async () => {
    fetchIntegrations();
  };

  const handleEdit = (integration) => {
    setEditIntegration(integration);
    setEditModalOpen(true);
  };

  const handleEditSave = () => {
    fetchIntegrations();
    setEditModalOpen(false);
    setEditIntegration(null);
  };

  const handleDelete = (integration) => {
    setDeleteTarget(integration);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (integrationId) => {
    try {
      await deleteIntegration(integrationId);
      alert("Integração excluída com sucesso");
    } catch (err) {
      alert("Falha ao excluir integração");
      throw err;
    }
  };

  const handleTestConnection = async (integration) => {

    if (!integration.credentials || !integration.credentials.summary) {
      alert('Configure as credenciais antes de testar');
      return;
    }

    setTestingConnections(prev => ({ ...prev, [integration.id]: true }));

    try {
      const response = await api.get(`/api/integrations/${integration.id}/test-connection`);
      const data = response.data;

      if (data.status === 'connected') {
        alert('Canal conectado com sucesso!');
        fetchIntegrations();
      } else if (data.status === 'disconnected') {

        if (data.error && data.error.includes('404')) {
          alert('Instância não encontrada!\n\nVerifique se os dados de integração estão corretos:\n• Nome da instância está correto\n• URL da Evolution API está correta\n• Instância foi criada no Evolution API Manager');
        } else if (data.error && data.error.includes('401')) {
          alert('Autenticação falhou!\n\nVerifique se os dados de integração estão corretos:\n• API Key está correta\n• API Key corresponde à configurada no Evolution API');
        } else if (data.error && (data.error.includes('timeout') || data.error.includes('ECONNREFUSED'))) {
          alert(' Servidor Evolution API não responde!\n\nVerifique se os dados de integração estão corretos:\n• Evolution API está online\n• URL está correta e acessível\n• Firewall não está bloqueando');
        } else {
          alert(`Desconectado\n\n${data.error || 'WhatsApp não está conectado nesta instância.\n\nAcesse o Evolution API Manager e escaneie o QR Code.'}`);
        }
      } else {
        alert(`${data.error || 'Erro ao conectar'}`);
      }
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;

        if (status === 404) {
          alert('Integração não encontrada no sistema');
        } else if (status === 401 || status === 403) {
          alert('Sessão expirada. Faça login novamente.');
          window.location.href = '/login';
        } else {
          alert(data.error || data.detail || 'Erro ao testar conexão');
        }
      } else if (error.request) {
        alert('Sem conexão com o servidor\n\nVerifique sua conexão com a internet');
      } else {
        alert('Erro ao testar conexão. Tente novamente.');
      }
    } finally {
      setTestingConnections(prev => ({ ...prev, [integration.id]: false }));
    }
  };


  const handleCloseConfigModal = () => {
    setActiveIntegration(null);
    setConfigModalType(null);
    fetchIntegrations();
  };

  const handleSaveIntegration = (providerKey, updatedIntegration) => {
    fetchIntegrations();
    setActiveIntegration(null);
    setConfigModalType(null);
  };

  const renderStatusBadge = (status) => {
    if (status === "connected") {
      return <span className="int-badge int-badge-connected">Conectado</span>;
    }
    if (status === "credentials_saved") {
      return <span className="int-badge int-badge-disconnected">Credenciais cadastradas</span>;
    }
    if (status === "soon") {
      return <span className="int-badge int-badge-soon">Em breve</span>;
    }
    return <span className="int-badge int-badge-disconnected">Credenciais cadastradas</span>;
  };


  const getProviderIcon = (provider) => {
    const iconKey = provider.icon_key || provider.type || "other";
    const Icon = ICON_MAP[iconKey] || ICON_MAP.other;
    return <Icon className="int-channel-icon" />;
  };


  const getProviderByKey = (key) => {
    return providers.find(p => p.key === key);
  };


  const filteredIntegrations = integrations.filter(integration => {
    if (filters.channel && integration.key !== filters.channel) {
      return false;
    }

    if (filters.label && !integration.label.toLowerCase().includes(filters.label.toLowerCase())) {
      return false;
    }


    if (filters.status && integration.status !== filters.status) {
      return false;
    }

    return true;
  });


  const totalPages = Math.ceil(filteredIntegrations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedIntegrations = filteredIntegrations.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const connectedCount = integrations.filter(i => i.status === "connected").length;
  const totalCount = integrations.length;
  const hasActiveFilters = filters.channel || filters.label || filters.status;

  if (loading || providersLoading) {
    return (
      <div className="Integrations">
        <FullScreenLoader message="Carregando Integrações..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="Integrations">
        <p className="int-error">{typeof error === 'string' ? error : error.message || 'Erro ao carregar integrações'}</p>
      </div>
    );
  }

  return (
    <div className="Integrations">
      <header className="int-header">
        <div>
          <p className="int-kicker">Integrações</p>
          <h1 className="int-title">
            Conecte o FASTTEAM aos seus canais principais
          </h1>
          <p className="int-subtitle">
            Gerencie suas integrações de WhatsApp, e-mail e redes sociais em um só lugar.
            Configure, atualize e monitore todas as suas conexões.
          </p>
        </div>
      </header>

      <section className="int-connected-section">
        <div className="int-connected-header">
          <div>
            <h2>Canais Cadastrados</h2>
            <p>
              {filteredIntegrations.length === 0 && hasActiveFilters
                ? "Nenhum canal encontrado com os filtros aplicados."
                : filteredIntegrations.length === 0
                  ? "Nenhum canal conectado ainda. Adicione seu primeiro canal abaixo."
                  : `${totalCount} ${totalCount === 1 ? 'canal cadastrado' : 'canais cadastrados'} / ${connectedCount} ${connectedCount === 1 ? 'conectado' : 'conectados'}`}
            </p>
          </div>
          <div className="int-connected-actions">
            <button
              className="int-filter-btn"
              onClick={() => setFilterModalOpen(true)}
              title="Filtrar canais"
            >
              <FiFilter />
              Filtros
              {hasActiveFilters && <span className="int-filter-badge"></span>}
            </button>
            <button
              className="int-add-new-btn"
              onClick={() => handleAddNew()}
            >
              <FiPlus />
              Adicionar Novo Canal
            </button>
          </div>
        </div>

        {paginatedIntegrations.length > 0 ? (
          <>
            <div className="int-connected-grid">
              {paginatedIntegrations.map(integration => {
                const provider = getProviderByKey(integration.key);
                return (
                  <div key={integration.id} className="int-connected-card">
                    <div className="int-connected-card-header">
                      <div className="int-connected-icon">
                        {provider ? getProviderIcon(provider) : <FaPlug className="int-channel-icon" />}
                      </div>
                      <div className="int-connected-info">
                        <h3>{integration.label}</h3>
                        <p className="int-connected-provider">
                          {provider?.name || integration.key}
                        </p>
                        {integration.credentials?.summary && (
                          <p className="int-connected-details">
                            {integration.credentials.summary.username ||
                              integration.credentials.summary.instance_name ||
                              integration.credentials.summary.sender_name}
                          </p>
                        )}
                      </div>
                      {renderStatusBadge(integration.status)}
                    </div>
                    <div className="int-connected-actions-row">
                      {(integration.key === 'whatsapp_evolution' || integration.key === 'site') && (
                        <button
                          onClick={() => handleTestConnection(integration)}
                          className="int-action-btn-small int-action-btn-test-small"
                          title="Testar Conexão"
                          disabled={testingConnections[integration.id] || !integration.credentials?.summary}
                        >
                          {testingConnections[integration.id] ? (
                            <>
                              <span className="int-spinner"></span>
                              Testando...
                            </>
                          ) : (
                            <>
                              <FiActivity />
                              Testar
                            </>
                          )}
                        </button>
                      )}

                      <button
                        onClick={() => handleEdit(integration)}
                        className="int-action-btn-small int-action-btn-primary-small"
                        title="Editar"
                      >
                        <FiEdit2 />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(integration)}
                        className="int-action-btn-small int-action-btn-danger-small"
                        title="Excluir"
                      >
                        <FiTrash2 />
                        Excluir
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="int-pagination-controls">
                <button
                  className="int-pagination-arrow"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  title="Página anterior"
                >
                  <FiChevronLeft />
                </button>
                <span className="int-pagination-info">
                  {currentPage} / {totalPages}
                </span>
                <button
                  className="int-pagination-arrow"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  title="Próxima página"
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="int-connected-empty">
            <div className="int-empty-icon">
              <FaPlug />
            </div>
            <h3>
              {hasActiveFilters ? 'Nenhum canal encontrado' : 'Nenhum canal conectado'}
            </h3>
            <p>
              {hasActiveFilters
                ? 'Tente ajustar os filtros ou limpe-os para ver todos os canais'
                : 'Comece adicionando seu primeiro canal de integração'}
            </p>
            {hasActiveFilters ? (
              <button
                className="int-add-first-btn"
                onClick={() => setFilterModalOpen(true)}
              >
                <FiFilter />
                Ajustar Filtros
              </button>
            ) : (
              <button
                className="int-add-first-btn"
                onClick={() => handleAddNew()}
              >
                <FiPlus />
                Adicionar Primeiro Canal
              </button>
            )}
          </div>
        )}
      </section>

      <section className="int-main">
        <div className="int-card">
          <div className="int-card-header">
            <div>
              <h2>Canais Disponíveis</h2>
              <p>
                Escolha um canal para adicionar uma nova integração. Você pode ter múltiplas contas do mesmo tipo.
              </p>
            </div>
          </div>

          <div className="int-channel-grid">
            {providers.map((provider) => {
              const providerIntegrations = integrations.filter(i => i.key === provider.key);
              const hasConnected = providerIntegrations.some(i => i.status === "connected");
              const status = provider.key === "instagram" ? "soon" : (hasConnected ? "connected" : "credentials_saved");

              return (
                <button
                  key={provider.key}
                  className={`int-channel-card ${provider.key === activeProviderKey ? "int-channel-card-active" : ""}`}
                  onClick={() => provider.key !== "instagram" && handleAddNew(provider.key, provider.name)}
                  type="button"
                  disabled={provider.key === "instagram"}
                >
                  <div className="int-channel-icon-wrap">
                    {getProviderIcon(provider)}
                  </div>
                  <span className="int-channel-name">{provider.name}</span>
                  <div className="int-channel-meta">
                    {renderStatusBadge(status)}
                    <span className="int-channel-count">
                      {providerIntegrations.length > 0
                        ? `${providerIntegrations.length} ${providerIntegrations.length === 1 ? 'conta' : 'contas'}`
                        : "Nenhuma conta"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="int-card int-side-card">
          <div className="int-card-header">
            <div>
              <h2>Como funciona a conexão</h2>
              <p>
                Cada canal tem um conjunto de credenciais próprio. As chaves
                são criptografadas e só são usadas pelos robôs do FASTTEAM.
              </p>
            </div>
          </div>
          <ul className="int-help-list">
            <li>
              <strong>1.</strong> Clique em "Adicionar Novo Canal" ou escolha um canal disponível.
            </li>
            <li>
              <strong>2.</strong> Dê um nome personalizado para identificar a conta (ex: Gmail Vendas).
            </li>
            <li>
              <strong>3.</strong> Configure as credenciais de acesso da conta.
            </li>
            <li>
              <strong>4.</strong> Use as contas nos módulos de CRM, Automação e Fluxos Inteligentes.
            </li>
          </ul>
          <p className="int-help-note">
            Você pode gerenciar permissões por workspace, time e módulo
            dentro das configurações avançadas.
          </p>
        </div>
      </section>


      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        providers={providers}
        currentFilters={filters}
        onApplyFilters={handleApplyFilters}
      />


      <AddIntegrationModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        providerKey={addModalProvider?.key}
        providerName={addModalProvider?.name}
        providers={providers}
        onCreate={handleCreateIntegration}
      />


      <EditIntegrationModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        integration={editIntegration}
        onSave={handleEditSave}
      />


      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        integration={deleteTarget}
        onDelete={handleDeleteConfirm}
      />


      {configModalType === "whatsapp_evolution" && activeIntegration && (
        <EvolutionWhatsApp
          isOpen={true}
          onClose={handleCloseConfigModal}
          providerKey={activeIntegration.key}
          integrationId={activeIntegration.id}
          env={activeIntegration.env}
          onSave={handleSaveIntegration}
          credentials={activeIntegration.credentials}
        />
      )}

      {configModalType === "email_smtp" && activeIntegration && (
        <Email
          isOpen={true}
          onClose={handleCloseConfigModal}
          providerKey={activeIntegration.key}
          integrationId={activeIntegration.id}
          env={activeIntegration.env}
          onSave={handleSaveIntegration}
          credentials={activeIntegration.credentials}
        />
      )}

      {configModalType === "site" && activeIntegration && (
        <SiteIntegration
          isOpen={true}
          onClose={handleCloseConfigModal}
          providerKey={activeIntegration.key}
          integrationId={activeIntegration.id}
          env={activeIntegration.env}
          onSave={handleSaveIntegration}
          credentials={activeIntegration.credentials}
        />
      )}

      {/* Modal de bloqueio por falta de pagamento */}
      <PaymentBlockModal
        isOpen={showBlockModal}
        onClose={closeBlockModal}
        actionName="adicionar integrações"
      />
    </div>
  );
};

export default Integrations;
